import { MessageSquare } from "lucide-react"
import { useCallback, useMemo } from "react"
import { useCodeStore } from "../../../store/context"
import type { Comment, CommentSource } from "../../../types"
import { type AnnotationSide, type CommentHandlers, FileViewer } from "../../FilesAndDiffs"
import { CopyTextButton, MarkdownMessage } from "../../MarkdownMessage"
import type { CommentContext, GroupRenderer, TextGroup } from "../../events/messageGroups"

function TextContent({ group, ctx }: { group: TextGroup; ctx: CommentContext }) {
    const codeStore = useCodeStore()
    const renderMarkdown = codeStore.personalSettingsStore?.settings.current.renderMarkdownMessages ?? true
    const sourceMatch = useCallback(
        (c: Comment) => {
            const src = c.source
            return src.type === "assistant_text" && src.actionEventId === ctx.actionEventId && src.messageIndex === group.messageIndex
        },
        [ctx.actionEventId, group.messageIndex]
    )

    const createSource = useCallback(
        (lineStart: number, lineEnd: number, _side: AnnotationSide): CommentSource => ({
            type: "assistant_text",
            actionEventId: ctx.actionEventId,
            messageIndex: group.messageIndex,
            lineStart,
            lineEnd,
        }),
        [ctx.actionEventId, group.messageIndex]
    )

    const commentHandlers: CommentHandlers = useMemo(() => ({ taskId: ctx.taskId, sourceMatch, createSource }), [ctx.taskId, sourceMatch, createSource])

    return (
        <div className="group/message-output relative min-w-0 pr-9">
            <CopyTextButton
                content={group.text}
                label="Copy message output"
                className="absolute top-0 right-0 z-20 opacity-0 transition-opacity group-hover/message-output:opacity-100 group-focus-within/message-output:opacity-100 [@media(hover:none)_and_(pointer:coarse)]:opacity-100"
            />
            {renderMarkdown ? (
                <MarkdownMessage text={group.text} commentHandlers={commentHandlers} variant="plain" density="compact" />
            ) : (
                <FileViewer file={{ name: "message.md", contents: group.text, lang: "markdown" }} disableFileHeader commentHandlers={commentHandlers} />
            )}
        </div>
    )
}

export const textRenderer: GroupRenderer<TextGroup> = {
    getLabel: () => "Response",
    getIcon: () => <MessageSquare size="0.85em" className="text-muted flex-shrink-0" />,
    getStatusIcon: () => null,
    getHeaderInfo: () => null,
    renderContent: (group, ctx) => <TextContent group={group} ctx={ctx} />,
}
